import React from 'react';
import { Box, Button, SxProps, Theme, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface AppPageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  /** Bouton retour à gauche du titre. */
  onBack?: () => void;
  backLabel?: string;
  /** Bouton d'action principal (à droite). */
  addLabel?: string;
  onAdd?: () => void;
  /** Slot libre d'actions supplémentaires (à droite, avant le bouton principal). */
  actions?: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * AppPageHeader — en-tête de page clair, harmonisé avec l'accueil
 * (titre foncé, pastille d'icône bleu pâle). Pendant clair de `PageHeader`.
 */
const AppPageHeader: React.FC<AppPageHeaderProps> = ({
  title, subtitle, icon, onBack, backLabel = 'Retour', addLabel, onAdd, actions, sx,
}) => (
  <Box
    sx={[
      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      {onBack && (
        <Button
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            textTransform: 'none', fontWeight: 600, color: '#344054',
            borderRadius: 2, px: 1.5, minWidth: 0,
            '&:hover': { bgcolor: '#eef0f3' },
          }}
        >
          {backLabel}
        </Button>
      )}
      {icon && (
        <Box
          sx={{
            width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
            bgcolor: '#eef4ff', color: '#2563eb',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '& .MuiSvgIcon-root': { fontSize: 22 },
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.15rem', md: '1.35rem' }, color: '#101828', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ mt: 0.25, fontSize: '0.85rem', color: '#667085' }}>{subtitle}</Typography>
        )}
      </Box>
    </Box>

    {(actions || (addLabel && onAdd)) && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {actions}
        {addLabel && onAdd && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAdd}
            disableElevation
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 2.25, py: 1,
              bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' },
            }}
          >
            {addLabel}
          </Button>
        )}
      </Box>
    )}
  </Box>
);

export default AppPageHeader;
